import { MimeType } from '@rad/components/inputs/file-input-with-previews';
/** The furthest a photo's frames zoom in, in percent of the size that fills the frame. */
export const MAX_ZOOM = 300;
export const IMAGE_MIME_TYPES = [MimeType.Png, MimeType.Jpeg, MimeType.Jpg, MimeType.Gif, MimeType.Webp];
export const isImage = (a) => IMAGE_MIME_TYPES.includes(a.mimeType);
/** The CSS object-position that keeps the photo's focal point in view in an object-fit: cover frame. */
export const focusPosition = (a) => `${a.focusX}% ${a.focusY}%`;
/** A smaller copy of a photo, labelled `w` and its width in pixels (App.Handlers.ImageVariants). */
const SIZE_VARIANT = /^w(\d+)$/;
/** The part of a photo its frames show when zoomed in, labelled `zoom`, the zoom in percent and the
 *  copy's width (App.Handlers.ImageVariants); a copy made before the widths has no `-w<width>`. */
const ZOOMED_COPY = /^zoom(\d+)(?:-w(\d+))?$/;
const sizeVariantWidth = (a) => {
    var _a;
    const match = SIZE_VARIANT.exec((_a = a.label) !== null && _a !== void 0 ? _a : '');
    return match ? Number(match[1]) : null;
};
const isZoomedCopy = (a) => { var _a; return ZOOMED_COPY.test((_a = a.label) !== null && _a !== void 0 ? _a : ''); };
export const defaultVariant = (a) => {
    var _a, _b;
    return (_b = (_a = a.attachments.find(v => v.isDefault)) !== null && _a !== void 0 ? _a : a.attachments.find(v => v.label !== 'header' && sizeVariantWidth(v) === null && !isZoomedCopy(v))) !== null && _b !== void 0 ? _b : a.attachments.find(v => sizeVariantWidth(v) === null && !isZoomedCopy(v));
};
/** The asset's zoom and the zoomed copies its frames show: the widest as the src, and all of them by
 *  width as a srcSet; nothing for a photo that isn't zoomed. */
export const zoomedCopy = (a) => {
    const copies = a.attachments
        .flatMap(v => {
        var _a;
        const match = ZOOMED_COPY.exec((_a = v.label) !== null && _a !== void 0 ? _a : '');
        return match ? [{ url: v.url, zoom: Number(match[1]), width: match[2] === undefined ? null : Number(match[2]) }] : [];
    })
        .sort((x, y) => { var _a, _b; return ((_a = x.width) !== null && _a !== void 0 ? _a : 0) - ((_b = y.width) !== null && _b !== void 0 ? _b : 0); });
    const widest = copies.at(-1);
    if (widest === undefined)
        return undefined;
    const byWidth = copies.flatMap(c => c.width === null ? [] : [`${c.url} ${c.width}w`]);
    return { zoom: widest.zoom, zoomedUrl: widest.url, zoomedSrcSet: byWidth.length > 0 ? byWidth.join(', ') : undefined };
};
/** The src and srcSet a frame shows a photo from: its zoomed copies when it is zoomed, otherwise the
 *  photo and its smaller copies. Either way the frame crops it around the focal point
 *  (focusPosition); a lightbox shows the whole photo from `url`. */
export const frameSource = (photo) => photo.zoomedUrl === undefined ? { src: photo.url, srcSet: photo.srcSet } : { src: photo.zoomedUrl, srcSet: photo.zoomedSrcSet };
/** The asset's smaller copies as an img srcSet, narrowest first; undefined for a photo that has none,
 *  which then shows its original. */
export const sizeVariantSrcSet = (a) => {
    const candidates = a.attachments
        .flatMap(v => { const width = sizeVariantWidth(v); return width === null ? [] : [{ url: v.url, width }]; })
        .sort((x, y) => x.width - y.width);
    return candidates.length > 0 ? candidates.map(c => `${c.url} ${c.width}w`).join(', ') : undefined;
};
export const headerVariant = (a) => a.attachments.find(v => v.label === 'header');
export const resolveHeaderUrl = (images, headerVariants) => {
    var _a, _b, _c, _d;
    const primary = (_b = (_a = images.find(a => a.isMain)) !== null && _a !== void 0 ? _a : images[0]) !== null && _b !== void 0 ? _b : null;
    const header = (primary === null || primary === void 0 ? void 0 : primary.assetId) ? headerVariants.find(h => h.assetId === primary.assetId) : undefined;
    return (_d = (_c = (header !== null && header !== void 0 ? header : primary)) === null || _c === void 0 ? void 0 : _c.url) !== null && _d !== void 0 ? _d : null;
};
export const IMAGE_CATEGORIES = ['exterior', 'interior', 'detail', 'historic', 'renovation', 'other', 'uncategorized'];
export const ATTACHMENT_CATEGORY = {
    exterior: { label: 'Exterior', icon: 'bi bi-house' },
    interior: { label: 'Interior', icon: 'bi bi-door-open' },
    detail: { label: 'Detail', icon: 'bi bi-zoom-in' },
    historic: { label: 'Historic', icon: 'bi bi-clock-history' },
    renovation: { label: 'Renovation', icon: 'bi bi-tools' },
    other: { label: 'Other', icon: 'bi bi-folder' },
    uncategorized: { label: 'Uncategorized', icon: 'bi bi-question-circle' },
};
export const CATEGORY_OPTIONS = Object.entries(ATTACHMENT_CATEGORY).map(([value, { label }]) => ({ value, label }));
